/* Atomz applied filters - what is currently on, as removable chips above the grid.
 *
 * THE PROBLEM: with the rail open you can see which groups hold a selection,
 * because the app floats those groups to the front and marks them. You cannot
 * see WHAT is selected without opening each one, and there is no way to take a
 * single value off without going back to the group that set it. With the rail
 * closed, on the chip bar or on a phone, even that much is gone.
 *
 * WHAT IT DOES NOT DO: it never applies or removes a filter itself. Each chip
 * holds a reference to the value row that set it and clicks that row, so removal
 * runs the app's own handler and every consequence - the URL, the refetch, the
 * group's own state - happens exactly as if the shopper had reopened the group
 * and clicked it there.
 *
 * WHY IT IS NOT IN .az-smart-filters: the app already has a row in the right
 * place, but it belongs to something else. It carries the filters the AI inferred
 * from the query rather than the ones the shopper chose, the app empties it on
 * every search, and it is left hidden (the classList.remove('az-hidden') that
 * would show it is commented out in az-search-page.js). Writing into it would
 * fight the app for a container on every keystroke.
 *
 * CATEGORY IS ABSENT ON PURPOSE. It is a place rather than a filter, so the
 * breadcrumb above the heading carries it and a chip here would say it twice.
 *
 * HOW IT KNOWS: a MutationObserver on the filter rail rather than the search
 * event. Selection state is a class on a row, and it changes on a plain click
 * with no event of its own; watching the DOM catches every path - the shopper's
 * click, a clear-all, and the re-render after a search - with no assumption
 * about which listener the app runs first.
 */
(function () {
  'use strict';

  if (!/\/search/.test(window.location.pathname)) return;

  var DEBOUNCE = 60;

  function grid() { return document.querySelector('atomz-product-grid'); }

  /* Every selected value in a real filter group. Scoping to .az-filter keeps
     out the app's own AI-detected chips, which carry the same classes but sit
     in .az-smart-filters, outside any group. */
  function selected() {
    return Array.prototype.slice
      .call(document.querySelectorAll('.az-filter .az-filter-item-value.selected'))
      .filter(function (el) {
        var group = el.closest('.az-filter');
        var id = group && group.querySelector('.az-filter-button');
        /*
         * The breadcrumb owns category. Matched loosely rather than on the
         * exact id "category", because with every facet enabled the app also
         * ships groups like product_category and attr_category, and each one
         * put a second copy of the breadcrumb's own value into this row.
         */
        return !(id && id.id && /categor/i.test(id.id));
      });
  }

  /*
   * One chip per value, not one chip per selected row.
   *
   * Several groups can carry the same value. With every facet enabled, Suede
   * is in both the material group and a second attribute group, so selecting
   * it once produced two identical "Suede" chips sitting side by side, and a
   * shopper clicking one watched the other stay put.
   *
   * Rows are grouped by their label and the chip removes all of them, so the
   * chip does what it looks like it does.
   */
  function byLabel(rows) {
    var order = [];
    var seen = {};
    rows.forEach(function (row) {
      var key = label(row).toLowerCase();
      if (!key) return;
      if (!seen[key]) {
        seen[key] = [];
        order.push(key);
      }
      seen[key].push(row);
    });
    return order.map(function (key) { return seen[key]; });
  }

  /*
   * Placed before the sort control rather than after the AI-detected chips,
   * because DOM order is the only order that survives.
   *
   * atomz-refine.css lays the app block out as an ordered column and gives the
   * row order: 4, which puts it above sort on desktop. But below 990px that
   * same file sets `body.az-filters-left .az-app-block { display: block }` for
   * the chip-bar layout, and a block container has no flex context, so every
   * order value stops applying at once and the column silently falls back to
   * source order - where sort is written before this row and therefore wins.
   *
   * Inserting ahead of sort satisfies both: source order carries mobile, and
   * order: 4 still carries desktop, where it also settles this row above the
   * AI-detected one it ties with.
   */
  function host() {
    var el = document.getElementById('az-applied');
    if (el) return el;

    var sort = document.querySelector('.az-sort-by-container');
    var after = document.querySelector('.az-smart-filters');
    var grid = document.getElementById('az-product-grid');

    el = document.createElement('div');
    el.id = 'az-applied';
    el.className = 'az-applied';

    if (sort && sort.parentNode) sort.parentNode.insertBefore(el, sort);
    else if (after && after.parentNode) after.parentNode.insertBefore(el, after.nextSibling);
    else if (grid && grid.parentNode) grid.parentNode.insertBefore(el, grid);
    else return null;

    return el;
  }

  function label(el) {
    /* The row renders as "Name (12)". The count belongs to the group, not to
       the chip, so take the text the span does not own. */
    var span = el.querySelector('span');
    var text = el.textContent || '';
    if (span && span.textContent) text = text.replace(span.textContent, '');
    return text.trim() || (el.dataset.value || '');
  }

  function chip(group) {
    var el = group[0];
    var b = document.createElement('button');
    b.type = 'button';
    b.className = 'az-applied__chip';

    var t = document.createElement('span');
    t.className = 'az-applied__label';
    t.textContent = label(el);
    b.appendChild(t);

    var x = document.createElement('span');
    x.className = 'az-applied__x';
    x.setAttribute('aria-hidden', 'true');
    b.appendChild(x);

    b.setAttribute('aria-label', 'Remove filter ' + t.textContent);

    /*
     * THE ROWS ARE FOUND AGAIN AT CLICK TIME, NOT HELD FROM RENDER TIME.
     *
     * A chip used to close over the row nodes it was built from. The app
     * rebuilds its rail on every search, every filter and every page of
     * results, so those nodes are replaced out from under the chip while the
     * chip is still on screen looking live. Clicking it then called .click()
     * on elements no longer in the document, which does exactly nothing and no
     * error -- the cross that does not work.
     *
     * Matching on the label rather than the node means the click always lands
     * on whatever is selected now. A row the app has since dropped simply is
     * not found, which is the honest outcome: there is nothing left to remove.
     */
    var key = t.textContent.toLowerCase();

    b.addEventListener('click', function () {
      var live = selected().filter(function (row) {
        return label(row).toLowerCase() === key;
      });
      if (!live.length) { schedule(); return; }

      /* Staggered. Each click makes the app re-render its rail, and clicking
         the second row in the same tick can land on a node it has replaced. */
      live.forEach(function (row, i) {
        window.setTimeout(function () { row.click(); }, i * 80);
      });
    });
    return b;
  }

  function render() {
    var rows = selected();
    var el = document.getElementById('az-applied');

    if (!rows.length) {
      if (el) el.remove();
      document.body.classList.remove('az-applied-on');
      return;
    }

    el = host();
    if (!el) return;
    el.innerHTML = '';

    byLabel(rows).forEach(function (group) { el.appendChild(chip(group)); });

    var clear = document.querySelector('.az-clear-filters');
    if (clear) {
      var all = document.createElement('button');
      all.type = 'button';
      all.className = 'az-applied__clear';
      all.textContent = 'Clear all';
      all.addEventListener('click', function () { clear.click(); });
      el.appendChild(all);
    }

    document.body.classList.add('az-applied-on');
  }

  var timer;
  function schedule() {
    window.clearTimeout(timer);
    timer = window.setTimeout(render, DEBOUNCE);
  }

  function watch() {
    /*
     * The app block, not the filter container.
     *
     * The container is a node the app is free to replace, and an observer
     * watching a node that gets replaced is watching a detached tree: it stops
     * firing, silently, and the chips freeze at whatever they last said. The
     * app block above it is the thing that stays put for the life of the page.
     */
    var target = grid() || document.querySelector('.az-app-block') ||
                 document.querySelector('.az-filters-container');
    if (!target) return false;
    new MutationObserver(schedule).observe(target, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['class']
    });
    schedule();
    return true;
  }

  if (!watch()) {
    /* The block renders its filters after this file runs, so wait for the
       container rather than giving up on it. */
    var boot = new MutationObserver(function () {
      if (watch()) boot.disconnect();
    });
    boot.observe(document.documentElement, { subtree: true, childList: true });
  }
})();
